import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FoonLogo, SearchIcon, MenuIcon, CartIcon } from "../page/assets";
import { RiCloseLine } from "react-icons/ri";
import DropdownMenu from "./DropdownMenu";
import SearchOverlay from "./SearchOverlay";
import CartOverlay from "./CartOverlay";
import { Link } from "react-router-dom";
import { useCartStore } from "../store/cartStore";
import { buildFoonPath } from "../utils/routes";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const idleTimeoutRef = useRef(null);

  const cartItems = useCartStore((state) => state.cartItems);
  const cartLength = cartItems.length;

  useEffect(() => {
    const clearIdleTimer = () => {
      if (idleTimeoutRef.current) {
        window.clearTimeout(idleTimeoutRef.current);
      }
    };

    const scheduleIdleHide = () => {
      clearIdleTimer();
      idleTimeoutRef.current = window.setTimeout(() => {
        if (
          window.scrollY > 100 &&
          !isMenuOpen &&
          !isSearchOpen &&
          !isCartOpen
        ) {
          setIsNavbarVisible(false);
        }
      }, 1000);
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setIsScrolled(currentScrollY > 50);
      setIsNavbarVisible(true);
      scheduleIdleHide();
    };

    setIsScrolled(window.scrollY > 50);
    scheduleIdleHide();
    window.addEventListener("scroll", handleScroll);
    return () => {
      clearIdleTimer();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMenuOpen, isSearchOpen, isCartOpen]);

  useEffect(() => {
    if (isMenuOpen || isSearchOpen || isCartOpen) {
      setIsNavbarVisible(true);
    }
  }, [isMenuOpen, isSearchOpen, isCartOpen]);

  const openSearch = () => {
    setIsMenuOpen(false);
    setIsCartOpen(false);
    setIsSearchOpen(true);
  };

  const openMenu = () => {
    setIsSearchOpen(false);
    setIsCartOpen(false);
    if (isCartOpen) {
      setIsMenuOpen(false);
      return;
    }
    setIsMenuOpen((prev) => !prev);
  };

  const openCart = () => {
    setIsSearchOpen(false);
    setIsMenuOpen(false);
    setIsCartOpen((prev) => !prev);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: isNavbarVisible ? 0 : -120,
          opacity: isNavbarVisible ? 1 : 0,
        }}
        transition={{ type: "spring", stiffness: 220, damping: 28, mass: 0.9 }}
        className={`fixed max-w-[1440px] mx-auto top-0 w-full ${
          isCartOpen ? "z-[60]" : "z-50"
        } transition-all duration-500 flex items-center justify-between px-8 md:!px-16 ${
          isScrolled && !isCartOpen
            ? "h-16 bg-white/70 backdrop-blur-md shadow-sm"
            : "h-[100px] bg-transparent"
        }`}
      >
        <Link
          to={buildFoonPath("/")}
          className={`flex items-center ${isCartOpen ? "max-lg:!opacity-0" : ""}`}
        >
          <FoonLogo />
        </Link>

        <div className="flex items-center gap-6 md:gap-8">
          {!isCartOpen && (
            <motion.button
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
              onClick={openSearch}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <SearchIcon className="w-5 h-5 text-black" />
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            onClick={openCart}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            className="flex relative items-center justify-center p-1"
          >
            <CartIcon className="w-5 h-5 text-black" />
            {cartLength > 0 && (
              <span className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 bg-[#22c55e] border border-white text-white text-[9px] w-[15px] h-[15px] rounded-full flex items-center justify-center font-mono">
                {cartLength}
              </span>
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            onClick={openMenu}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            className="flex items-center"
          >
            {isMenuOpen || isCartOpen ? (
              <RiCloseLine className="w-6 h-6 text-black" />
            ) : (
              <MenuIcon className="w-5 h-5 text-black" />
            )}
          </motion.button>
        </div>
      </motion.nav>

      <AnimatePresence mode="wait">
        {isSearchOpen && (
          <SearchOverlay key="search" onClose={() => setIsSearchOpen(false)} />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {isMenuOpen && (
          <DropdownMenu
            key="menu"
            isOpen={isMenuOpen}
            isScrolled={isScrolled}
            onClose={() => setIsMenuOpen(false)}
            isNavbarVisible={isNavbarVisible}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {isCartOpen && (
          <CartOverlay
            key="cart"
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
